/**
 * The vocabulary of a work.
 *
 * These values must stay in step with the enums in
 * supabase/migrations/20260803110000_collections.sql and
 * 20260804090700_works_without_review.sql. Nothing enforces that but this
 * comment — the same standing arrangement as `domain/collection.ts`.
 */
/**
 * What a photograph of a work shows.
 *
 * `ondo.image_kind` predates this file: it was built for the photographs a
 * collector attached to a SUBMISSION, and it outlived the submission when
 * 20260804090700 hung the images off the work instead. The vocabulary did not
 * need changing, which is the argument for having kept it: a verso is a verso
 * whoever is looking.
 */
export const IMAGE_KINDS = [
    "front",
    "back",
    "signature",
    "stamp",
    "gallery_label",
    "edition_number",
    "detail",
    "certificate",
    "other",
];
/**
 * Which series of an edition a copy belongs to.
 *
 * Must stay in step with `ondo.edition_copy_class` in
 * supabase/migrations/20261014094000_a_cast_is_a_work_and_an_edition_is_not_an_object.sql.
 * Nothing enforces that but this comment — the standing arrangement of this
 * file and of `domain/collection.ts`.
 *
 * ORDERED AS THE ENUM IS, numbered first, because that is the one series
 * `edition_size` bounds. The order is not a ranking: Andrew's answer of 28
 * September 2026 is that proofs and hors-commerce copies are counted
 * ALONGSIDE the numbered edition size and never within it, so AP 2 is not a
 * lesser 2 — it is a different copy from numbered 2, and both may exist.
 */
export const EDITION_COPY_CLASSES = [
    "numbered",
    "artist_proof",
    "hors_commerce",
    "unnumbered",
];
export const isEditionCopyClass = (value) => typeof value === "string" && EDITION_COPY_CLASSES.includes(value);
/**
 * What a copy of an edition is called on a label: "3/7", "AP 2", "HC".
 *
 * THE ONE PLACE THIS SENTENCE IS WRITTEN, which is the point of it being here
 * rather than in SQL. `ondo.work_edition()` returns the PARTS — the class, the
 * number, the declared size — and no rendered string, because a rendered string
 * in a `returns table` is a presentation decision made in the database, and two
 * renderers drift (the arrangement `merkleRoot` is pinned to literal vectors to
 * stop).
 *
 * THE DENOMINATOR APPEARS ONLY FOR THE NUMBERED SERIES, and only when the
 * edition declares a size. There is no declared proof-series size anywhere in
 * the schema, so "AP 2/4" is deliberately not printable: a denominator derived
 * from how many proof rows happen to exist would read "AP 2/3" at the moment
 * three of four were recorded, which is a false statement about somebody's
 * property. See 20261014094000 decision 10.
 */
export const editionCopyNumbering = (copy) => {
    const { copyClass, copyNumber } = copy;
    if (copyClass === "unnumbered")
        return null;
    const prefix = copyClass === "artist_proof" ? "AP" : copyClass === "hors_commerce" ? "HC" : "";
    if (copyNumber === null)
        return prefix === "" ? null : prefix;
    // Only the numbered series has a declared total to be out of.
    const size = copyClass === "numbered" ? (copy.editionSize ?? null) : null;
    const figure = size === null ? `${copyNumber}` : `${copyNumber}/${size}`;
    return prefix === "" ? figure : `${prefix} ${figure}`;
};
/**
 * The displayed identity Andrew's second answer asks for: "the edition code
 * plus the copy number".
 *
 * `editionCode` IS NULL FOR A CALLER WHO HOLDS A COPY BUT NOT THE EDITION, and
 * that is not an error state to guard against — it is `ondo.work_edition()`
 * being SECURITY INVOKER, so `works_read` decides whether the edition's code is
 * disclosed at all (20261014094000 decision 12). The copy's OWN code is always
 * readable by definition, so that is what this falls back to, and the sentence
 * stays true either way: a collector reads their cast's own code and its number
 * in the edition.
 */
export const editionCopyIdentity = (copy) => {
    const code = copy.editionCode ?? copy.ownCode ?? null;
    const numbering = editionCopyNumbering(copy);
    if (code === null)
        return numbering;
    return numbering === null ? code : `${code} ${numbering}`;
};
//# sourceMappingURL=work.js.map